/**
 * Zod schemas + DTO types for the Projects API surface — Phase 4 W3.T3.1.
 *
 * Source-of-truth: `.planning/phases/04-projects-clients/CONTEXT.md` (Q01,
 * Q02, Q05, Q22, Q41) + `.planning/phases/04-projects-clients/PLAN.md`
 * §"Wave 3" + §"NS-1" + §"S-7" + §"S-9" + §"MF-1".
 *
 * IMPORTANT (MF-1 fix):
 *   - The field set below is VERBATIM-LOCKED to CONTEXT/PLAN.
 *     `research-frontend.md §6.2` is HISTORICAL — do not consult it.
 *   - Q01: Project gains `startDate Date NULL` only.
 *     NO `type`, NO `budget`, NO `description` in M1.
 *   - Q02: `status` enum is exactly { ACTIVE | ARCHIVED | COMPLETED }.
 *     NO `PAUSED`. (Mockup tab «На паузе» dropped.)
 *   - Q41: pagination is offset-based — `?limit&offset`. NO `?page`.
 *     Server hard-caps `offset ≤ 5000` (S-9); the FE schema enforces the
 *     same ceiling so an over-deep page request short-circuits client-side.
 *
 * NS-1 round-3 (defence-in-depth on PATCH-status):
 *   - `ProjectUpdateSchema` KEEPS the `status` field with a `.refine()`
 *     rejecting `'ARCHIVED'` and `'COMPLETED'` → 422
 *     `STATUS_TRANSITION_SCHEMA_REJECT`. Stripping the field would make
 *     the service-layer guard dead code (DA-R2 NS-1).
 *   - The service layer still re-checks ARCHIVED→ACTIVE / COMPLETED→ACTIVE
 *     transitions (T2.2) with a distinct code
 *     `STATUS_TRANSITION_VIA_DEDICATED_ENDPOINT` for direct service-call
 *     paths (G9.bis).
 */
import { z } from 'zod';
import { AddressSchema, ComplexNameSchema, ContractStatus } from './contract';
import { AccessLevel, ProjectStatus, UserStatus } from './enums';
import { ruEnum } from './lib/zod-ru';

// ─── Служебный объект «Внутренние работы» ─────────────────────────────────

/**
 * Canonical code for the catch-all («служебный») project bootstrapped by
 * `ProjectsBootstrapService` (W3.20 / R6.15).
 *
 * Single source of truth: `apps/api/src/projects/projects-bootstrap.service.ts`,
 * `apps/api/src/bot-vkteams/flows/report.flow.ts`, and the web modal
 * (`apps/web/src/components/workload/workload-cell-modal.tsx`) all
 * resolve the catch-all by this literal. A rename anywhere else WILL
 * silently break the picker submenu lookup — exporting the constant
 * keeps the dependency explicit at the type/import level.
 *
 * Persisted as the canonical `Project.code` value; partial-unique index
 * `project_code_other_unique` (migration 031) guarantees at most one
 * row carries it.
 *
 * ⚠️ Индекс гарантирует НЕ БОЛЕЕ одной такой строки, а не ровно одну:
 * служебного проекта может не быть вовсе (загрузчик пропускает создание,
 * если нет внутреннего клиента или живого администратора). Потребитель
 * обязан переживать его отсутствие внятным отказом, а не пустым пикером.
 *
 * Блок 2 · Ф4 · В-ВР: код `OTHER` РЕЗЕРВИРОВАН — `POST /projects` и
 * `PATCH /projects/:id` его отвергают. Причина в том, что признак
 * служебности выражается именно кодом (решение Р-10 + §3.2 плана Ф4):
 * клиентский проект, успевший занять `OTHER`, стал бы для загрузчика
 * каноническим служебным и молча выпал бы из всех списков.
 */
export const MISC_PROJECT_CODE = 'OTHER';

/**
 * Наименование служебного объекта — «Внутренние работы» (решение
 * заказчика Р-9 от 11.08.2026; прежнее имя — «Прочее»).
 *
 * ⚠️ ЖИВЁТ В ОБЩЕМ ПАКЕТЕ, А НЕ НА СЕРВЕРЕ, И ЭТО НЕСУЩЕЕ. До Ф4 · В-ВР
 * константа была объявлена в `projects-bootstrap.service.ts`, то есть
 * фронту недоступна — а весь класс «слово „Прочее" значит два разных
 * предмета» живёт именно на фронте: «Прочее» — это ещё и подпись лейна
 * БЕЗ РАЗДЕЛА в загрузке (`calendar-grid.tsx`, `workload-cell-modal.tsx`,
 * `heatmap-day-detail-modal.tsx`). Переименование грепом по строке
 * сломало бы их все, и сломало бы тихо: подписи остались бы осмысленными,
 * а значить стали бы не то. Отсюда правило: имя служебного объекта
 * упоминается ТОЛЬКО через эту константу, по обе стороны.
 *
 * Значение в базе переутверждается загрузчиком на каждом старте
 * контейнера (`ensureMiscProject`) — правка одной этой строки меняет имя
 * и на свежей установке, и на уже живущем стенде.
 */
export const MISC_PROJECT_NAME = 'Внутренние работы';

/**
 * НЕВИДИМАЯ МЕТКА служебной ГРУППЫ справочника — значение `SectionCatalogItem.systemKey`
 * у корневой строки «Внутренние работы» (решение **Р-93** от 25.08.2026, ответы 1 и 5).
 *
 * ⚠️ ОДНА КОЛОНКА НА ДВЕ НУЖДЫ Р-93 — ЭТО СОЗНАТЕЛЬНОЕ СЛИЯНИЕ, И РЕШЕНО ОНО
 * ВОЛНОЙ, А НЕ ВЛАДЕЛЬЦЕМ. Р-93 прямо говорит: «признак „служебная группа"
 * (ответ 1) и метка стартового вида (ответ 5) — ДВЕ РАЗНЫЕ НУЖДЫ, и сливать их
 * в одно поле нельзя молча». Слиты они потому, что **механизм узнавания обязан
 * быть один**: экран отличает служебную группу от каталожной, а загрузчик —
 * стартовый вид от добавленного супервайзером, и если это два разных признака,
 * они разойдутся молча (переименовали одно, забыли другое — и экран показывает
 * то, чего загрузчик уже не узнаёт). Уровни при этом различаются НЕ значением,
 * а `parentId`: у группы он `NULL`, у пункта — заполнен.
 *
 * ⚠️ КОД СТАБИЛЕН И ОТ ИМЕНИ НЕ ЗАВИСИТ — в этом весь смысл ответа 5: «как виды
 * НАЗВАНЫ — дело супервайзера». Переименование группы или пункта метку не
 * трогает; узнавание идёт по коду, а не по строке имени.
 */
export const INTERNAL_WORK_GROUP_SYSTEM_KEY = 'INTERNAL_WORK';

/**
 * СТАРТОВЫЙ состав видов внутренней работы — ОТОБРАЖЕНИЕ «код → стартовое имя»
 * (решение **Р-90** от 25.08.2026, «Виды внутренней работы заводит загрузчик, а
 * не человек»; переделано из плоского списка имён решением **Р-93**, ответ 5).
 *
 * ⚠️ КЛЮЧ ИДЕМПОТЕНТНОСТИ ЗАГРУЗЧИКА — `systemKey`, А НЕ `name`, И ЭТО НЕСУЩЕЕ.
 * Пока ключом было имя, «вид убрали» (ответ 2 Р-93 требует **завести**) и «вид
 * переименовали» (ответ 4 требует **не трогать**) были для системы одним и тем
 * же наблюдением — «пункта с таким именем нет», — а действия под ними
 * противоположны. Без метки два ответа не исполняются одновременно ни при каком
 * старании исполнителя. Прогон круга ревью это предъявил: «Обучение» →
 * «Обучение и развитие» дало ВТОРОЙ активный вид на объекте.
 *
 * ⚠️ ВТОРОГО ЛИТЕРАЛА КОДОВ НЕТ И БЫТЬ НЕ ДОЛЖНО — граница 6 решения Р-70.
 * Коды читают: миграция `061` (DML разметки уже существующих строк — там они
 * записаны SQL-строками по необходимости, но сверены с этим перечнем),
 * загрузчик (`ProjectsBootstrapService.ensureInternalWorkKinds`), сервис
 * справочника (вторая точка записи привязки) и экран выбора разделов.
 *
 * ⚠️ ПЕРЕЕХАЛО СЮДА ИЗ `apps/web/src/i18n/work.ts` (Ф5 · В9-d), И ПЕРЕЕХАЛО
 * ПО ТРЕБОВАНИЮ, А НЕ ДЛЯ ПОРЯДКА. До Р-90 состав читал только фронт —
 * подсказка в форме задачи; после Р-90 его читает ЗАГРУЗЧИК в `apps/api`
 * (`ProjectsBootstrapService.ensureInternalWorkKinds`). Второй литерал в
 * втором месте **отклонён прецедентом Р-70, граница 6** дословно: «два
 * литерала в двух местах отклонены здесь» — копия разошлась бы при первом
 * же переименовании, и разошлась бы молча. Отсюда общий пакет: одна строка
 * на обе стороны.
 *
 * ⚠️ ЧЕТВЁРТЫЙ ВИД — «Разное», А НЕ «Прочее», И ЭТО НЕ СИНОНИМ. «Прочее»
 * уже занято на том же экране другим предметом: так подписан ЛЕЙН БЕЗ
 * РАЗДЕЛА в раскрытой строке проекта (`SECTION_MISC_LABEL`,
 * `calendar-grid.tsx`, `heatmap-day-detail-modal.tsx`). Полный разбор — у
 * `internalWorkRu.kinds`, который теперь только переэкспортирует это
 * значение.
 *
 * ⚠️ СОСТАВ ОТКРЫТЫЙ, А НЕ ЗАКРЫТЫЙ (Р-90, положение 3, дословно владелец:
 * «виды внутренней работы супервайзер может дополнять через интерфейс»).
 * Это СТАРТОВЫЙ список, а не эталон: загрузчик им ДОПОЛНЯЕТ справочник и
 * НЕ ВЫРАВНИВАЕТ по нему (ловушка 1 Р-90). Кто прочтёт эту константу как
 * «полный перечень допустимого» и станет по ней сверять — снесёт на
 * ближайшем старте всё, что добавил супервайзер, вместе с часами, которые
 * на добавленное списаны.
 *
 * ⚠️ `Проверка-ВВР-1310` СЮДА НЕ ВХОДИТ И ВХОДИТЬ НЕ ДОЛЖЕН — это мусор
 * старой проверки, уже лежащий в справочнике стенда (долг #149). Кто
 * станет сверять стенд с этим составом, найдёт лишний пункт: это
 * ожидаемо, а не дефект. ⚠️ **С Р-93 у этой строки появилась вторая, более
 * дорогая цена:** пункт, получивший метку, становится НЕСНОСИМЫМ (ответ 2 —
 * снятое загрузчик воскрешает). Разметить «всех детей группы» значило бы
 * сделать несносимым и `Проверка-ВВР-1310`, и ошибка эта МОЛЧИТ.
 */
export const INTERNAL_WORK_KIND_SEED = [
  { systemKey: 'INTERNAL_WORK:MEETINGS', name: 'Совещания' },
  { systemKey: 'INTERNAL_WORK:TRAINING', name: 'Обучение' },
  { systemKey: 'INTERNAL_WORK:ADMIN', name: 'Административное' },
  { systemKey: 'INTERNAL_WORK:MISC', name: 'Разное' },
] as const;

/** Одна строка стартового состава: код метки плюс СТАРТОВОЕ (не текущее) имя. */
export type InternalWorkKindSeed = (typeof INTERNAL_WORK_KIND_SEED)[number];

/**
 * Прежний экспорт — только ИМЕНА стартового состава, в том же порядке.
 *
 * ⚠️ ОСТАВЛЕН ДЛЯ ТЕХ, КОМУ НУЖЕН СПИСОК, А НЕ КЛЮЧИ (подсказка в форме задачи
 * — `internalWorkRu.kinds`), и ВЫВОДИТСЯ из отображения выше, а не пишется
 * рядом с ним: две редакции одного состава однажды разойдутся.
 *
 * ⚠️ ЭТО СТАРТОВЫЕ ИМЕНА, А НЕ ТЕКУЩИЕ. После Р-93 (ответ 4, «уважать
 * переименование») супервайзер вправе переименовать любой вид, и в базе будет
 * стоять ЕГО имя, а здесь — то, с которого начали. Кто станет сверять экран с
 * этим списком, увидит расхождение и примет его за дефект.
 */
export const INTERNAL_WORK_KINDS: readonly InternalWorkKindSeed['name'][] =
  INTERNAL_WORK_KIND_SEED.map((k) => k.name);

/**
 * ДВЕ ПОДПИСИ ОДНОГО ПОЛЯ — «Вид работы» у служебного объекта, «Раздел» у
 * обычного. Третьего имени не заводить.
 *
 * ⚠️ ПЕРЕЕХАЛО СЮДА ИЗ `apps/web/src/i18n/work.ts` (Ф5 · В9-b) ПО ТОЙ ЖЕ
 * ПРИЧИНЕ И ПО ТОМУ ЖЕ ПРЕЦЕДЕНТУ, ЧТО И `INTERNAL_WORK_KIND_SEED` ВЫШЕ.
 * Основание — **Р-70, граница 6**: пара существует в дереве с Ф4, и бот обязан
 * её ВЗЯТЬ, а не завести свою. Читателей стало двое: экран формы задачи
 * (`apps/web`) и пикер раздела в боте (`apps/api`, `report.flow.ts`), а словарь
 * лежал под `apps/web`, куда `apps/api` не смотрит. Два литерала в двух местах
 * отклонены решением — копия разошлась бы на первом переименовании, и
 * разошлась бы молча.
 *
 * ⚠️ ЗНАЧЕНИЯ НЕ МЕНЯЛИСЬ НИ ОДНОЙ БУКВОЙ — те же две строки, что стояли в
 * `internalWorkRu.sectionLabel` и `taskFormRu.sectionLabel`. Волна переносит
 * место, а не текст: переезд, попутно поправивший подпись, был бы правкой
 * экрана под видом рефакторинга.
 *
 * ⚠️ ПОДПИСЬ ВЫБИРАЕТСЯ ПРИЗНАКОМ СЛУЖЕБНОСТИ (`isInternalProjectRow`,
 * то есть КОДОМ объекта), а НЕ сравнением имени с литералом: имена супервайзер
 * вправе менять (**Р-93**, ответ 4), и отбор по имени разъехался бы с базой при
 * первом же переименовании.
 *
 * ⚠️ ЭТО НЕ `SECTION_MISC_LABEL` («Прочее» — лейн БЕЗ раздела) и не название
 * строки «Без раздела» в пикере бота. Три разных предмета, и путать их дорого:
 * первый — подпись ПОЛЯ, второй — подпись ЛЕЙНА в загрузке, третий — СТРОКА
 * списка.
 */
export const SECTION_FIELD_LABEL = {
  /** Служебный объект: раздел там — это вид внутренней работы, а не раздел. */
  internal: 'Вид работы',
  /** Объект заказчика. */
  regular: 'Раздел',
} as const;

// ─── Shared primitives ────────────────────────────────────────────────────

/** UUID — RFC-4122 string. Mirrors the workload-module style. */
const UuidSchema = z.string().uuid();

/**
 * Strict YYYY-MM-DD date string. Calendar validity is BE-owned (Postgres
 * `$1::date` cast); FE only checks shape so the contract is single-sourced.
 */
const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
  message: 'Дата должна быть в формате ГГГГ-ММ-ДД',
});

/** ISO-8601 datetime (createdAt / updatedAt / archivedAt). */
const IsoDateTimeSchema = z.string().datetime({ offset: true });

/** Project status enum, mirrored from prisma + `enums.ts`. */
export const ProjectStatusSchema = z.enum(
  [ProjectStatus.ACTIVE, ProjectStatus.ARCHIVED, ProjectStatus.COMPLETED],
  ruEnum('Выберите статус'),
);
export type ProjectStatusValue = z.infer<typeof ProjectStatusSchema>;

/**
 * Ярлыки вкладок карточки объекта — `/projects/:id?tab=...`.
 *
 * ⚠️ ЭТА СХЕМА НИКЕМ НЕ ПОТРЕБЛЯЕТСЯ (проверено поиском по репозиторию:
 * ссылок на неё нет ни одной). Страница держит собственный список
 * (`projects-detail.page.tsx`, `ALL_TABS`), и именно он — действующий.
 *
 * Поэтому схема РАСХОДИЛАСЬ С ЖИЗНЬЮ и молча: она знала `'workload'`, снятую
 * ещё в R6 W3, знала `'team'`, слитую с `'sections'` в Ф3 · В3, и не знала
 * самой `'sections'`. Расхождение приведено в соответствие (П-12 круга ревью).
 * Схема оставлена, а не удалена: она — единственное место, где состав вкладок
 * объявлен ДАННЫМИ, и следующий потребитель (бот, ссылки уведомлений Ф5)
 * возьмёт его отсюда. Если такого потребителя не появится к концу блока —
 * удалить, а не подновлять снова.
 */
export const ProjectTabSchema = z.enum([
  'overview',
  // Ф3 · В3: «Команда» и «Разделы» слиты в один экран «Разделы и команда».
  'sections',
  'journal',
  'unplanned',
  // M2 «Финблок» Фича 2 — flat project expenses (e.g. subcontractor cost).
  // Уровню MANAGER не показывается (Ф3 · В3, T3.8 — решение 08.08.2026).
  'expenses',
]);
export type ProjectTab = z.infer<typeof ProjectTabSchema>;

// ─── Pagination envelope ──────────────────────────────────────────────────

/**
 * Generic offset-based pagination envelope (Q41). Used by /projects,
 * /clients, and /projects/:id/audit. NB: `data` (not `rows`) per round-4
 * alignment with `AuditService.listForProject`.
 *
 * Re-exported via `index.ts` so `clients.ts` (and any future module) can
 * compose with the same shape.
 */
export const paginatedSchema = <T extends z.ZodTypeAny>(item: T) =>
  z.object({
    data: z.array(item),
    total: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
  });

// ─── List query ───────────────────────────────────────────────────────────

/**
 * `GET /projects` query schema. Coercion via `z.coerce.*` because Express
 * query params are strings.
 *
 * Constraints:
 *   - `limit` ∈ [1, 100], default 25.
 *   - `offset` ∈ [0, 5000] — S-9 hard-cap (server also enforces with 422
 *     `DEEP_OFFSET_FORBIDDEN`; FE schema short-circuits at the same cap).
 *   - `status` accepts the 3-enum + `'ALL'` literal (controller-side
 *     convention to bypass the default-active filter; CONTEXT Q22).
 *   - `clientId` is ADMIN-only at runtime (S-7); the schema permits the
 *     shape, the service rejects non-ADMIN actors with 403.
 *   - NO `page`. NO `sortBy`/`sortDir` (deferred to Phase 5+).
 */
/**
 * W3.20 (R6.15) — visibility scope override.
 *
 *   `assigned` (default) — backward-compatible per-actor scope
 *     applied by `projectVisibilityWhere` (EMPLOYEE sees only own
 *     participations; MANAGER only own РО/ГИП; ADMIN all).
 *   `allActive`           — widen to every ACTIVE + not-archived
 *     project regardless of actor (used by the bot's «Другой
 *     проект» submenu and the web modal's «Другие проекты»
 *     group). The server still rejects SUPERVISOR (no project
 *     access at all) and forces `status=ACTIVE` so the surface
 *     does not leak archived rows.
 */
export const ProjectsScopeSchema = z.enum(
  ['assigned', 'allActive'],
  ruEnum('Недопустимая область выборки'),
);
export type ProjectsScope = z.infer<typeof ProjectsScopeSchema>;

export const ListProjectsQuerySchema = z
  .object({
    limit: z.coerce.number().int().min(1).max(100).default(25),
    offset: z.coerce.number().int().min(0).max(5000).default(0),
    // Объединение схлопывает ошибки веток в родовой `invalid_union`, и
    // дефолтный текст zod для него — английское «Invalid input». Значения
    // при этом не утекают (ветки закрыты своими сообщениями), но на
    // поверхности, где остальные сообщения русские, человек видел
    // английскую строку. Свой errorMap на узле объединения.
    status: z
      .union([ProjectStatusSchema, z.literal('ALL')], ruEnum('Недопустимый статус'))
      .optional(),
    search: z.string().trim().min(1).max(200).optional(),
    clientId: UuidSchema.optional(),
    scope: ProjectsScopeSchema.optional(),
    /**
     * Ф2 · В2c — фильтр по комплексу. Сравнение ЦЕЛИКОМ, а не по вхождению:
     * значение приходит из выпадающего списка уже существующих написаний
     * (`GET /projects/complex-names`), и «содержит» склеило бы «Северный» с
     * «Северный-2».
     *
     * ⚠️ Р-149 (16.09.2026) — «ЦЕЛИКОМ» БОЛЬШЕ НЕ ЗНАЧИТ «ПОБУКВЕННО». Здесь
     * стояло «сравнение ТОЧНОЕ», и после Р-149 это неправда: сервер отбирает по
     * КЛЮЧУ (`normalizeComplexName` — без регистра и лишних пробелов), то есть
     * `?complexName=жк%20парковый` находит и объекты «ЖК Парковый». Прежняя
     * формулировка отправила бы следующего читателя строить клиентское
     * сравнение строк — ровно то расхождение предикатов, которое Р-149 и лечит.
     *
     * Схема `.strict()`, поэтому поле обязано быть объявлено здесь: без этого
     * запрос с фильтром получил бы ОТКАЗ, а не тихо проигнорированный параметр.
     */
    complexName: z.string().trim().min(1).max(200).optional(),
    /**
     * Блок 2 · Ф4 · В-ВР — показывать ли в основной выдаче служебный объект
     * «Внутренние работы» (`code = 'OTHER'`). По умолчанию он из неё СКРЫТ.
     *
     * ⚠️ ПАРАМЕТР НУЖЕН ПОТОМУ, ЧТО СОКРЫТИЕ И ЕДИНСТВЕННОЕ РАБОЧЕЕ
     * ПРИМЕНЕНИЕ СЛУЖЕБНОГО ПРОЕКТА — ЭТО ОДНА ВЫДАЧА СЕРВЕРА. Предикат,
     * поставленный в `listPaginated` без разводящего параметра, отнял бы
     * служебный проект у окна отчёта о часах, и внутренние часы стало бы
     * некуда списывать — молча, без ошибки и без записи в журнале.
     *
     * Разведено так: `scope=allActive` (окно факта, подменю бота) остаётся
     * БАЙТ-В-БАЙТ прежним и сокрытия не знает вовсе; параметр действует
     * только на основную выдачу. Отсюда же выбор формы — отдельный флаг, а
     * не третье значение `scope`: третье значение пришлось бы объяснять
     * каждому существующему потребителю `scope`, тогда как флаг ортогонален
     * и не трогает ни одного.
     *
     * ⚠️ РАСШИРЕНИЯ ВИДИМОСТИ ЭТОТ ФЛАГ НЕ ДАЁТ и потому уровнем доступа не
     * ограничен: он лишь ОТМЕНЯЕТ ВЫЧИТАНИЕ поверх `projectVisibilityWhere`
     * и не возвращает ни одной строки, которой актор не видел до Ф4.
     * Сравнить с `clientId`: тот открывает канал «какие объекты у этого
     * клиента» и потому ADMIN-only. Здесь такого канала нет.
     *
     * Значение приходит строкой в query-string, поэтому разбирается как
     * `'true' | 'false'`, а НЕ через `z.coerce.boolean()`: последний
     * приводит любую непустую строку к `true`, то есть `?includeInternal=false`
     * значил бы «включить».
     */
    includeInternal: z
      .enum(['true', 'false'], ruEnum('Недопустимое значение фильтра'))
      .transform((v) => v === 'true')
      .optional(),
  })
  .strict();
export type ListProjectsQuery = z.infer<typeof ListProjectsQuerySchema>;

/**
 * `GET /projects/complex-names` — существующие написания комплексов.
 * Плоский список строк: он кормит и фильтр списка, и комбо-бокс карточки.
 */
export const ComplexNamesResponseSchema = z.array(z.string());
export type ComplexNamesResponse = z.infer<typeof ComplexNamesResponseSchema>;

// ─── Read shapes ──────────────────────────────────────────────────────────

/**
 * Compact project summary returned by list endpoints + embed contexts.
 * Field set is VERBATIM-LOCKED per Q01 (PLAN-MF-1):
 *   - id, name, code (nullable), status, startDate (NEW Q01), deadline,
 *     archivedAt, gip {id, fullName}, client {id, name},
 *     participantCount, version.
 *   - NO `type`, NO `budget`, NO `description`, NO `kpp`.
 *
 * ⚠️ ЗАМОК СНЯТ ЧАСТИЧНО — Блок 2 · Ф2 · В2c. Прежняя редакция запрещала
 * `address` и обещала «набор полей закреплён дословно». Первое перестало быть
 * верным раньше самой правки: колонку `Project.address` завела миграция 048
 * (Ф2 · В1) вместе с `complexName` и `contractId`, и запрет в комментарии
 * относился к набору полей ОТВЕТА М1, а не к таблице.
 *
 * В2c добавляет в ответ ТРИ поля:
 *   - `complexName` — группировка «комплекс» (решение заказчика 12: поле, а не
 *     справочник; отдельной модели `Complex` не заводится вовсе);
 *   - `effectiveDeadline` — ВЫЧИСЛЯЕМОЕ `contract.deadline ?? project.deadline`
 *     (решение 13: договорной срок главнее собственного). В базе его нет;
 *   - `address` — адрес/площадка объекта (ТЗ-5.3-a), колонка миграции 048.
 *
 * ⚠️ `address` В ПЛАНЕ В2c НЕ ЗНАЧИЛСЯ, и это расхождение названо, а не
 * замолчано: §2 В2c и ветка 2c общего плана перечисляют для правки объекта
 * ТОЛЬКО `complexName`, тогда как комментарий `AddressSchema` (заведённой
 * волной В1, в `contract.ts`) обещает маршрут правки именно в В2c. Колонка
 * заведена, обещание записано, пути записи не было ни одного — схема-сирота.
 * Решение оркестратора 08.08.2026: включить, и тем же ходом, что и привязку
 * договора, — это ОДИН класс, и разный ответ на две его половины закрепил бы
 * расхождение без причины.
 */
export const ProjectSummarySchema = z.object({
  id: UuidSchema,
  name: z.string(),
  code: z.string().nullable(),
  status: ProjectStatusSchema,
  startDate: IsoDateSchema.nullable(),
  deadline: IsoDateSchema.nullable(),
  archivedAt: IsoDateTimeSchema.nullable(),
  gip: z.object({
    id: UuidSchema,
    fullName: z.string(),
  }),
  client: z.object({
    id: UuidSchema,
    name: z.string(),
  }),
  participantCount: z.number().int().nonnegative(),
  version: z.number().int().nonnegative(),
  /** Ф2 · В2c — «комплекс» как группировка объектов. `null` = без комплекса. */
  complexName: z.string().nullable(),
  /** Ф2 · В2c — адрес/площадка объекта (ТЗ-5.3-a). `null` = не указан. */
  address: z.string().nullable(),
  /**
   * Ф2 · В2c — `contract.deadline ?? project.deadline` (решение 13).
   *
   * ВЫЧИСЛЯЕТСЯ, В БАЗЕ НЕ ХРАНИТСЯ. Оператор именно `??`, а не «есть договор —
   * берём его срок»: у привязанного договора срок может быть пуст, и тогда
   * действует собственный срок объекта. Тернарник по наличию договора дал бы в
   * этом случае `null` — то есть стёр бы срок с карточки.
   */
  effectiveDeadline: IsoDateSchema.nullable(),
});
export type ProjectSummary = z.infer<typeof ProjectSummarySchema>;

/** Alias kept for FE consumers that prefer the «list-item» mental model. */
export const ProjectListItemSchema = ProjectSummarySchema;
export type ProjectListItem = z.infer<typeof ProjectListItemSchema>;

/**
 * W3.20 fix-pack v3 (DA-BLOCKER #1) — slim picker shape returned by
 * `GET /projects?scope=allActive` to NON-ADMIN actors. Strictly
 * `{ id, name, code }`: zero metadata leakage of gip / client / dates
 * / participantCount / status / archivedAt to the EMPLOYEE picker
 * surface. ADMIN actors keep the full `ProjectSummarySchema` shape
 * even with `scope=allActive` so admin tooling does not regress.
 *
 * Consumers (`workload-cell-modal` «Другие проекты» group + bot
 * «Другой проект» submenu) require only these three fields to render
 * and route. The wire shape is intentionally narrower than the FE's
 * `ProjectListItem` type so callers must explicitly opt into the
 * slim envelope — see `ProjectPickerListResponseSchema` below.
 */
export const ProjectPickerItemSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  code: z.string().nullable(),
});
export type ProjectPickerItem = z.infer<typeof ProjectPickerItemSchema>;

/**
 * Discriminated `GET /projects` envelope for the slim picker surface.
 * Mirrors the shape of `ProjectListResponseSchema` so the pagination
 * fields (`total / limit / offset`) remain meaningful to the picker,
 * but `data[]` is the slim `ProjectPickerItem`.
 */
export const ProjectPickerListResponseSchema = paginatedSchema(ProjectPickerItemSchema);
export type ProjectPickerListResponse = z.infer<typeof ProjectPickerListResponseSchema>;

/**
 * Full project detail — Summary + managers + participants. Returned by
 * `GET /projects/:id`. Department id is `number | null` to mirror the
 * workload module's row shape (Phase 3 W1.T2).
 */
export const ProjectDetailSchema = ProjectSummarySchema.extend({
  /**
   * Ф2 · В2c — ссылка на договор объекта. `null` = договор не привязан.
   *
   * ⚠️ `sum` ЗДЕСЬ НЕТ И БЫТЬ НЕ МОЖЕТ — не «не заполнен», а отсутствует в
   * типе. Решение заказчика 07.08.2026 («Только сумму») закрывает от
   * руководителя отдела ровно её; номер, статус, дату и срок он видит. Сумма
   * живёт исключительно в `ContractWithSumResponse` на `/contracts`, и
   * ветвление по правам делает ТОТ сервис.
   *
   * Ссылка не гейтится уровнем ВООБЩЕ: номер, статус, дату и срок получает
   * каждый, кому видна карточка объекта, включая рядового сотрудника. Это
   * РЕШЕНИЕ ОРКЕСТРАТОРА (08.08.2026), а не заказчика: он отвечал про
   * руководителя отдела, про сотрудника его не спрашивали. Не сузили
   * сознательно — сотрудник ведёт работу по договорному сроку, и
   * `effectiveDeadline` без источника читался бы как срок ниоткуда. Разбор и
   * место возможного сужения — `apps/api/src/projects/projects.service.ts`,
   * объявление `ContractRefRow`.
   */
  contract: z
    .object({
      id: UuidSchema,
      number: z.string(),
      status: z.nativeEnum(ContractStatus),
      date: IsoDateSchema.nullable(),
      deadline: IsoDateSchema.nullable(),
      /**
       * Ф7 · В11-b, фикс-пак круга ревью — СНЯТ ЛИ ДОГОВОР СО СПРАВОЧНИКА.
       *
       * ⚠️ ЭТО ВТОРАЯ ОСЬ, А НЕ ПЕРЕСКАЗ `status`. Архивный договор может
       * оставаться «в работе», а закрытый — жить в справочнике; справочник
       * договоров показывает обе, карточка объекта показывала только первую.
       * Расхождение достижимо: объект архивируют, его договор архивируют (без
       * согласия на отвязку — живых объектов нет), объект восстанавливают, —
       * и карточка печатает «В работе» там, где справочник печатает «Архив».
       *
       * ⚠️ ОТБОР ПО НЕМУ НЕ ВЕДЁТСЯ НИГДЕ: архивный договор объект не
       * покидает, и `effectiveDeadline` по-прежнему считается от его срока
       * (`projects.service.ts`). Поле сообщает факт, а не меняет правило.
       */
      archivedAt: IsoDateTimeSchema.nullable(),
    })
    .nullable(),
  managers: z.array(
    z.object({
      id: UuidSchema,
      fullName: z.string(),
      departmentId: z.number().int().nullable(),
    }),
  ),
  participants: z.array(
    z.object({
      id: UuidSchema,
      fullName: z.string(),
      departmentId: z.number().int().nullable(),
      role: z.enum([
        AccessLevel.EMPLOYEE,
        AccessLevel.MANAGER,
        AccessLevel.ADMIN,
        AccessLevel.SUPERVISOR,
      ]),
      // Account lifecycle — the section-assign picker filters to ACTIVE,
      // non-archived candidates (the BE assign gate is the authority via
      // USER_NOT_ASSIGNABLE; the picker mirrors it so a deactivated/archived
      // participant is never offered).
      status: z.nativeEnum(UserStatus),
      archivedAt: IsoDateTimeSchema.nullable(),
    }),
  ),
  // R5 / Phase 7: derived field. Lists departments whose project still has
  // active participants but no remaining ProjectManager row. FE surfaces a
  // banner «Отдел X без РО — добавление сотрудников этого отдела недоступно».
  // Privacy-gate (DA-9): only populated when actor is ADMIN-tier (or the
  // current ГИП); empty array for EMPLOYEE / MANAGER viewers to avoid leaking
  // org-chart structure. Always serialised (default []), never null.
  // Department.id is Int (autoincrement, see prisma schema), NOT uuid.
  departmentsWithoutManager: z
    .array(
      z.object({
        id: z.number().int(),
        name: z.string(),
      }),
    )
    .default([]),
});
export type ProjectDetail = z.infer<typeof ProjectDetailSchema>;

/**
 * Top-level alias requested by Wave 3 brief — full project shape (parity
 * with `ProjectDetailSchema`). FE feature code can pick whichever name
 * communicates intent best.
 */
export const ProjectSchema = ProjectDetailSchema;
export type Project = z.infer<typeof ProjectSchema>;

/** `GET /projects` envelope. */
export const ProjectListResponseSchema = paginatedSchema(ProjectListItemSchema);
export type ProjectListResponse = z.infer<typeof ProjectListResponseSchema>;

// ─── Write requests ───────────────────────────────────────────────────────

/**
 * `POST /projects` body — VERBATIM-LOCKED per Q01.
 *
 * NO `type`. NO `budget`. NO `managerIds`. NO `description`.
 *
 * Phase 4 FIX-PACK FT.B5 (closes CR-MF-5):
 *   - `gipId` (required UUID) added — BE controller `CreateProjectSchema`
 *     requires it but the shared schema previously omitted it, so the FE
 *     create flow could not produce a valid wire body. Project creation
 *     was therefore broken end-to-end.
 *   - `startDate` removed — BE controller is `.strict()` and does NOT
 *     accept `startDate` on create today. Keeping it in the schema would
 *     cause a 400 if the FE sent the field. The wire row's `startDate`
 *     remains nullable on read; future PATCH support is out of FB scope.
 *
 * Refinement Q05 retained on the deadline pair-check is dropped here
 * because there is no startDate to compare against on create. The same
 * refine still lives on `ProjectUpdateSchema`.
 */
export const ProjectCreateSchema = z
  .object({
    name: z.string().trim().min(2).max(200),
    code: z.string().trim().max(50).optional(),
    gipId: UuidSchema,
    clientId: UuidSchema,
    deadline: IsoDateSchema.optional(),
    /**
     * Р-146 (16.09.2026) — адрес/площадка, НЕОБЯЗАТЕЛЬНОЕ поле, и оно
     * появляется ЗДЕСЬ, а не только в правке.
     *
     * ⚠️ БЕЗ ЭТОЙ СТРОКИ ПОЛЕ ПРОПАДАЛО БЫ МОЛЧА. Серверная
     * `CreateProjectSchema` — `.strip()`: незнакомый ключ не вызывает отказа,
     * его просто нет в разобранном теле. Человек заполнил бы адрес при
     * заведении объекта, получил бы «Проект создан» и пустую строку в
     * карточке. Поэтому Р-146 — это правка ТРЁХ мест (общая схема,
     * контроллер, сервис), а не одной формы.
     */
    address: AddressSchema.optional(),
    /**
     * ⚠️ Ф7 · В11-b, ФИКС-ПАК 3 — РЕШЕНИЕ ОРКЕСТРАТОРА (владельцу названо в
     * отчёте волны): комплекс и договор принимаются и ПРИ ЗАВЕДЕНИИ объекта.
     *
     * До этого оба поля жили только в правке, и форма заведения их не рисовала
     * — с честной оговоркой «маршрут создания их не принимает». Оговорка была
     * верна и ровно поэтому неудобна: человек заводил объект и тут же открывал
     * его правку, чтобы указать комплекс и договор. Р-146 уже прошёл этим путём
     * для адреса (общая схема + контроллер + сервис); здесь тот же ход.
     *
     * Аддитивно: поля необязательны, ни один существующий вызывающий их не
     * шлёт, форма правки не тронута. Нормализация — те же схемы, что на правке
     * (`ComplexNameSchema` схлопывает пробелы и приводит `''` к `null`).
     *
     * ⚠️ ПОДСТАНОВКУ СУЩЕСТВУЮЩЕГО НАПИСАНИЯ (Р-149) ДЕЛАЕТ СЕРВИС — и на
     * заведении тоже. Схема о ней не знает и знать не может: правило требует
     * запроса в базу.
     */
    complexName: ComplexNameSchema.optional(),
    /** `null` — объект заводится без договора; это же значение и умолчание. */
    contractId: UuidSchema.nullable().optional(),
  })
  .strict();
export type ProjectCreate = z.infer<typeof ProjectCreateSchema>;

/**
 * `PATCH /projects/:id` body. `version` REQUIRED (optimistic-lock).
 *
 * NS-1 round-3: `status` is KEPT in the schema with a `.refine()` that
 * rejects `'ARCHIVED'` and `'COMPLETED'`. Mapping to 422 with
 * `STATUS_TRANSITION_SCHEMA_REJECT` happens at the controller layer
 * (G9 e2e). Service layer enforces the same intent for direct-call paths
 * with code `STATUS_TRANSITION_VIA_DEDICATED_ENDPOINT` (G9.bis).
 *
 * Why two distinct codes: the schema-layer error tells callers the SHAPE
 * is wrong (use `POST /:id/archive` etc.); the service-layer error
 * additionally guarantees correctness for non-HTTP entry points (jobs,
 * future RPC). Keeping both also satisfies the "defence-in-depth" rule
 * in NS-1 §3 FI-1.
 */
/**
 * ⚠️ Ф7 · В11-b — `complexName` и `contractId` ДОБАВЛЕНЫ ЗДЕСЬ, И ЭТО
 * РАСХОЖДЕНИЕ ПЛАНА С КОДОМ, А НЕ РАСШИРЕНИЕ ДОГОВОРА.
 *
 * Контроллер принимает оба поля с волны Ф2 · В2c (`UpdateProjectSchema`,
 * `projects.controller.ts` — `complexName: ComplexNameSchema.optional()`,
 * `contractId: Uuid.nullable().optional()`), а ЭТА схема — та, по которой
 * типизирован веб (`ProjectUpdate` → `useUpdateProjectMutation`), — про них не
 * знала. То есть путь записи существовал на сервере и был НЕДОСТИЖИМ из
 * типизированного клиента: любая форма, пославшая эти поля, не собиралась бы.
 *
 * План В11-b говорил «`packages/shared` не трогается (схемы договора и списка
 * готовы)» — про схемы ДОГОВОРА и СПИСКА это верно (`contract.ts`,
 * `ListProjectsQuerySchema`), а про DTO правки объекта — нет. Расхождение
 * названо в отчёте волны, а не замолчано.
 *
 * ⚠️ ЗДЕСЬ СТОЯЛО «`address` СОЗНАТЕЛЬНО НЕ ДОБАВЛЕН» — И ЭТО УСТАРЕЛО РАНЬШЕ,
 * ЧЕМ БЫЛО ДОЧИТАНО: поле `address` объявлено десятком строк НИЖЕ, в этой же
 * схеме, решением Р-146 того же дня. Текст спорил с собственным кодом, и
 * следующий читатель либо счёл бы код ошибкой, либо перестал бы верить
 * комментариям файла. Довод («предмет волны — договор и комплекс») был верен на
 * момент написания и отменён решением владельца, а не забыт.
 *
 * ⚠️ `ComplexNameSchema` — та же схема, что у контроллера, а не её пересказ:
 * она нормализует пустую строку в `null` (форма всегда шлёт `''`, и без
 * нормализации очистить поле было бы нечем) и зеркалит CHECK
 * `project_complex_name_length`. Второе описание одного правила однажды
 * разошлось бы с первым молча.
 */
const ProjectUpdateBaseSchema = z
  .object({
    version: z.number().int().nonnegative(),
    name: z.string().trim().min(2).max(200).optional(),
    code: z.string().trim().max(50).nullable().optional(),
    clientId: UuidSchema.optional(),
    startDate: IsoDateSchema.nullable().optional(),
    deadline: IsoDateSchema.nullable().optional(),
    status: ProjectStatusSchema.optional(),
    complexName: ComplexNameSchema.optional(),
    /**
     * Р-146 — адрес/площадка. Контроллер принимал его с Ф2 · В2c, а ЭТА
     * схема (по ней типизирован веб) — нет: путь записи существовал и был
     * недостижим из типизированного клиента. Тот же класс, что и у
     * `complexName`/`contractId` выше, и закрывается тем же ходом.
     *
     * Пустая строка нормализуется в `null` внутри `AddressSchema` — это и
     * есть способ ОЧИСТИТЬ поле: форма всегда шлёт строку.
     */
    address: AddressSchema.optional(),
    /** `null` отвязывает договор со стороны объекта. */
    contractId: UuidSchema.nullable().optional(),
  })
  .strict();

export const ProjectUpdateSchema = ProjectUpdateBaseSchema.refine(
  (input) => input.status !== 'ARCHIVED' && input.status !== 'COMPLETED',
  {
    message:
      'Архивировать или завершить проект можно только соответствующим действием в его карточке',
    path: ['status'],
    params: { code: 'STATUS_TRANSITION_SCHEMA_REJECT' },
  },
).refine((input) => !input.startDate || !input.deadline || input.startDate <= input.deadline, {
  message: 'Дата старта не может быть позже срока сдачи',
  path: ['startDate'],
});
export type ProjectUpdate = z.infer<typeof ProjectUpdateSchema>;

// ─── Dedicated mutation bodies ────────────────────────────────────────────

/**
 * `POST /projects/:id/restore` body. `version` REQUIRED — restoring an
 * archived project bumps the row, so optimistic lock is part of the
 * contract (T2.4 / FI-3).
 */
export const RestoreProjectSchema = z
  .object({
    version: z.number().int().nonnegative(),
  })
  .strict();
export type RestoreProject = z.infer<typeof RestoreProjectSchema>;

/**
 * `POST /projects/:id/participants/promote-from-fact` body (Q11 NR-1).
 * Used to promote a fact-only reporter (an employee who has logged FACT
 * hours on the project but is not a participant) into a participant.
 */
export const PromoteFromFactSchema = z
  .object({
    userId: UuidSchema,
  })
  .strict();
export type PromoteFromFact = z.infer<typeof PromoteFromFactSchema>;

// ─── Audit (B-1 — `/projects/:id/audit`) ──────────────────────────────────

/**
 * Single «Журнал» entry — R6.5 «humanize project journal».
 *
 * Pre-R6.5 the endpoint returned raw `audit_log` rows (entity/action/
 * before/after) and the FE attempted prose rendering. That approach
 * failed in practice — see ProjectHistoryPanel:140 (the old branch
 * compared `entity === 'Project'` but the audit-extension persists
 * `project` in snake_case, so every live row fell through to a
 * defensive «{verb} {entity}» fallback). R6.5 flipped the contract:
 * the BE renders the Russian description, the FE just displays it.
 *
 * The forensic raw audit trail still lives in `audit_log` and is
 * exposed via the global admin `/audit` endpoint — it is NOT removed.
 * Only the per-project «Журнал» response shape changed.
 */
export const ProjectAuditEventSchema = z.object({
  /** Stable `audit_log.id` — React key for the row. */
  id: UuidSchema,
  /** ISO-8601 timestamp; FE formats in Europe/Moscow. */
  at: z.string(),
  /** Actor's fullName, or `null` for system actions (jobs / seeds). */
  actorName: z.string().nullable(),
  /** Pre-rendered Russian sentence ready for direct display. */
  description: z.string(),
});
export type ProjectAuditEvent = z.infer<typeof ProjectAuditEventSchema>;

/** `GET /projects/:id/audit` envelope — offset-based per round-4 (T1.7). */
export const ProjectAuditListResponseSchema = paginatedSchema(ProjectAuditEventSchema);
export type ProjectAuditListResponse = z.infer<typeof ProjectAuditListResponseSchema>;

// ─── R6 W3 — Project «Сотрудники» panel (team-fact-hours) ─────────────────

/**
 * `GET /projects/:id/team-fact-hours` row — one entry per project
 * participant with their accumulated fact-hours total on the project.
 *
 * Cohort: `ProjectParticipant` only (managers / ГИП live in the top panel
 * via `project.managers[]`). A user who is BOTH a participant and a
 * manager on the same project still appears here, intentionally — they
 * DO log fact-hours as a participant.
 *
 * Field notes:
 *   - `deptId` is the integer Department FK (QA-S-2). FE compares it
 *     against `viewer.departmentId` for the «Удалить» RBAC gate.
 *   - `deptId` is `nullable()` because `User.departmentId` is `Int?`.
 *     In practice EMPLOYEE participants always have a department, but
 *     ADMIN/MANAGER participants (rare edge case) may not — keep the
 *     schema honest.
 *   - `deptName` mirrors `deptId` semantics; service replaces a NULL
 *     join with literal `'—'` so the FE has a stable display string.
 *   - `factHoursTotal` is a NUMBER (not Decimal/string). Service casts
 *     Postgres NUMERIC → JS number before returning (safe in practice
 *     because per-project totals fit comfortably in JS-number precision;
 *     the SQL clamps to NUMERIC(10,1)).
 */
/**
 * Разрез часов сотрудника ПО РАЗДЕЛУ объекта (Ф3 · В3, T3.7, ПРИЁМКА-3).
 *
 * ─── ПОЧЕМУ ЭТО НЕ «ПЕРЕЕЗД ПАНЕЛИ» ──────────────────────────────────────
 *
 * Оси раздела у `ProjectTeamFactHoursRow` не было ВОВСЕ — строка отдавала итог
 * по человеку. Слить вкладки, перенеся панель как есть, значило бы закрыть
 * критерий «часы в разрезе разделов» зелёным на панели, которая разрезов не
 * показывает.
 *
 * ─── `sectionId: null` — ЗАКОННАЯ СТРОКА РАЗБИВКИ, А НЕ ОСТАТОК ──────────
 *
 * Так приходят часы, записанные без раздела («Прочее», в том числе всё
 * доразделочное наследие). Спрятав их в разность «итог минус сумма строк», мы
 * получили бы невидимую корзину, в которую тихо стекает всё непонятое.
 *
 * ─── ИНВАРИАНТ: Σ ПО `sections[]` = `factHoursTotal` ─────────────────────
 *
 * Держится ДВУМЯ вещами сразу, и обе несущие: обе выборки идут в одной
 * транзакции `RepeatableRead` (снимок один — параллельная запись часов не может
 * лечь между ними), а `WorkloadFact.hours` — `Decimal(4,1)`, то есть суммы по
 * разделам и их итог точны на одном знаке и дрейфа округления не дают. Разбор
 * того же класса ошибки — у `ProjectUnplannedSectionSchema` ниже, форма запроса
 * и ответа взята оттуда же.
 *
 * `factCount` здесь НЕТ намеренно (в отличие от «Внепланово»): панель считает
 * часы, а не записи, и лишнее поле пришлось бы поддерживать в инварианте,
 * ничего не давая экрану.
 */
export const ProjectTeamFactHoursSectionSchema = z.object({
  sectionId: UuidSchema.nullable(),
  /**
   * `.nullable()` БЕЗ `.min(1)` — тот же довод, что у «Внепланово» (U4):
   * непустое имя обеспечено при заведении раздела, но схема ответа не должна
   * становиться точкой отказа целой панели. Неожиданная `''` иначе уронила бы
   * разбор ВСЕГО ответа и обнулила панель. `null` = «Прочее».
   */
  sectionName: z.string().nullable(),
  factHoursTotal: z.number().nonnegative(),
});
export type ProjectTeamFactHoursSection = z.infer<typeof ProjectTeamFactHoursSectionSchema>;

export const ProjectTeamFactHoursRowSchema = z.object({
  userId: UuidSchema,
  fullName: z.string().min(1),
  deptId: z.number().int().nullable(),
  deptName: z.string().min(1),
  factHoursTotal: z.number().nonnegative(),
  /**
   * Разрез по разделам (Ф3 · В3, T3.7). `.default([])` — идиома совместимости
   * той же формы, что у «Внепланово»: ответ без поля продолжает разбираться
   * (→ пустая разбивка), а выведенный тип несёт гарантированный массив.
   *
   * ⚠️ ПУСТОЙ МАССИВ — ЗАКОННОЕ СОСТОЯНИЕ. Когорта строится по
   * `ProjectParticipant` через LEFT JOIN, то есть участник БЕЗ единого часа
   * тоже даёт строку — с нулевым итогом и пустой разбивкой. Инвариант Σ = итог
   * на нём выполняется тождественно (0 = 0), и тест обязан включать этот
   * случай: без него «сумма сходится» доказано только там, где часы есть.
   */
  sections: z.array(ProjectTeamFactHoursSectionSchema).default([]),
});
export type ProjectTeamFactHoursRow = z.infer<typeof ProjectTeamFactHoursRowSchema>;

/**
 * `GET /projects/:id/team-fact-hours` response — bare array. Endpoint is
 * NOT paginated (per PLAN §6.3 W3-BE-T2 — `LIMIT 500` is a defensive
 * safety net, not a paging contract).
 */
export const ProjectTeamFactHoursResponseSchema = z.array(ProjectTeamFactHoursRowSchema);
export type ProjectTeamFactHoursResponse = z.infer<typeof ProjectTeamFactHoursResponseSchema>;

/**
 * `GET /projects/:id/unplanned` row — one entry per user who logged
 * `WorkloadFact` rows on the project but is NOT in `ProjectParticipant`
 * (the «Внеплановые» tab cohort, surfaced via a NOT-EXISTS anti-join).
 *
 * Mirrors {@link ProjectTeamFactHoursRowSchema} conventions so the
 * «Внеплановые» tab can render the same row UI (avatar + ФИО over dept +
 * right-aligned hours) and reuse `ProjectEmployeeReportsModal`.
 *
 * Field notes:
 *   - `deptId` is the integer Department FK; `nullable()` because
 *     `User.departmentId` is `Int?` (rare for an unplanned contributor,
 *     but kept honest).
 *   - `deptName` mirrors `deptId`; the service replaces a NULL join with
 *     literal `'—'` so the FE always has a stable display string.
 *   - `factCount` — number of `WorkloadFact` rows the user logged on the
 *     project (secondary muted label).
 *   - `factHoursTotal` is a NUMBER (not Decimal/string). Service casts
 *     Postgres NUMERIC → JS number before returning (per-project totals
 *     fit comfortably in JS-number precision).
 *   - `sections` (Block 2 · 01a · T2.6) — per-section breakdown of the same
 *     facts, one entry per `(user, sectionId)` bucket. `sectionId: null` =
 *     «Прочее» (facts logged without a section, incl. every legacy row from
 *     before раздел-time). INVARIANT: `factCount`/`factHoursTotal` above
 *     equal the Σ over `sections[]` — `hours` is `Decimal(4,1)`, so the
 *     per-section sums and their total are exact at one decimal and the
 *     top-level fields never regress.
 */

/**
 * One per-section bucket inside a {@link ProjectUnplannedRowSchema} row.
 * `sectionId: null` / `sectionName: null` denote «Прочее» (unattributed
 * facts). `factHoursTotal` is a NUMBER (service casts NUMERIC → JS number).
 */
export const ProjectUnplannedSectionSchema = z.object({
  sectionId: UuidSchema.nullable(),
  // `.nullable()` WITHOUT `.min(1)` on purpose (U4): W1 enforces a non-empty
  // name at create time, so a blank name should never occur — but the response
  // schema must not become a tab-wide failure point. An unexpected '' would
  // otherwise throw the WHOLE `unplanned` parse and blank the tab; a lenient
  // string keeps the surface resilient. `null` = «Прочее».
  sectionName: z.string().nullable(),
  factCount: z.number().int().nonnegative(),
  factHoursTotal: z.number().nonnegative(),
});
export type ProjectUnplannedSection = z.infer<typeof ProjectUnplannedSectionSchema>;

export const ProjectUnplannedRowSchema = z.object({
  userId: UuidSchema,
  fullName: z.string().min(1),
  deptId: z.number().int().nullable(),
  deptName: z.string().min(1),
  factCount: z.number().int().nonnegative(),
  factHoursTotal: z.number().nonnegative(),
  // Additive (T2.6). `.default([])` keeps parsing backwards-compatible: a
  // pre-T2.6 payload without the field still parses (→ empty breakdown),
  // while the inferred output type carries a guaranteed array.
  sections: z.array(ProjectUnplannedSectionSchema).default([]),
});
export type ProjectUnplannedRow = z.infer<typeof ProjectUnplannedRowSchema>;

/**
 * `GET /projects/:id/unplanned` response — bare array. NOT paginated
 * (the service `LIMIT 1000` is a defensive safety net; the tab paginates
 * client-side).
 */
export const ProjectUnplannedResponseSchema = z.array(ProjectUnplannedRowSchema);
export type ProjectUnplannedResponse = z.infer<typeof ProjectUnplannedResponseSchema>;

// ─── Блок 2 · Ф8 · В-З1 — человек объекта БЕЗ часов ─────────────────────────

/**
 * Строка `team-fact-hours` / `unplanned` для того, у кого право «вся
 * загруженность бюро» ОТНЯТО (Р-208; решение оркестратора по фикс-паку 1
 * волны В-З1, по образцу Р-212: «закрываются часы, а не люди»).
 *
 * Человек в ответе есть — состав объекта и «кто списал вне состава» не часы:
 * без них ГИП не исключит участника и не включит внепланового в команду.
 * ЧИСЕЛ НЕТ ВОВСЕ: ключей `factHoursTotal`, `factCount` и `sections` в строке
 * не существует. Не `0` и не `null` — ноль читался бы как «не работал».
 *
 * `hoursHidden: true` — признак, по которому потребитель различает две формы,
 * не полагаясь на отсутствие ключа.
 *
 * ⚠️ Прежние схемы строк и ответов НЕ менялись: это новая форма рядом.
 * Потребитель, типизированный прежним ответом, при отнятом праве получит
 * строки этой формы — различать обязан по {@link isProjectHoursHiddenRow}.
 */
export const ProjectHoursHiddenRowSchema = z
  .object({
    userId: UuidSchema,
    fullName: z.string().min(1),
    deptId: z.number().int().nullable(),
    deptName: z.string().min(1),
    hoursHidden: z.literal(true),
  })
  .strict();
export type ProjectHoursHiddenRow = z.infer<typeof ProjectHoursHiddenRowSchema>;

/** Ответ `team-fact-hours`, как его отдаёт сервер: с часами либо люди без часов. */
export const ProjectTeamFactHoursAnyResponseSchema = z.union([
  ProjectTeamFactHoursResponseSchema,
  z.array(ProjectHoursHiddenRowSchema),
]);
export type ProjectTeamFactHoursAnyResponse = z.infer<typeof ProjectTeamFactHoursAnyResponseSchema>;

/** Ответ `unplanned`, как его отдаёт сервер: с часами либо люди без часов. */
export const ProjectUnplannedAnyResponseSchema = z.union([
  ProjectUnplannedResponseSchema,
  z.array(ProjectHoursHiddenRowSchema),
]);
export type ProjectUnplannedAnyResponse = z.infer<typeof ProjectUnplannedAnyResponseSchema>;

/** Строка без часов? Признак — поле `hoursHidden`, а не отсутствие суммы. */
export function isProjectHoursHiddenRow(row: object): row is ProjectHoursHiddenRow {
  return 'hoursHidden' in row && row.hoursHidden === true;
}
